import React, { FC, useMemo, useRef, useState } from 'react';
import './ResultGraph.css';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FaChartBar, FaChartLine, FaExpand, FaTimes } from 'react-icons/fa';
import moment from 'moment';
import { groupBy } from '@/Shared/Helpers';
import Modal from '../Modal/Modal';
import { Dropdown, GraphSeriesFilter } from '..';
import { useModal } from '@/Shared/Logic';

export type ChartSerie = {
	key: keyof Record<string, any>;
	color: string;
	type: 'Line' | 'Bar';
};

export type DateGranularity = 'day' | 'month' | 'year';

interface ResultGraphProps {
	data: Record<string, any>[],
	keyToLabelMap: Record<keyof Omit<Record<string, any>, 'date'>, string>,
	chartSeries: ChartSerie[],
	title: string
}

const ResultGraph: FC<ResultGraphProps> = ({ data, keyToLabelMap, chartSeries, title }) => {

	//const [isModalOpen, setIsModalOpen] = useState(false);
	const modalHook = useModal();
	const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set(chartSeries.map(s => s.key)));
	const [dateGranularity, setDateGranularity] = useState<DateGranularity>('day');

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);


	const aggregatedData = useMemo(() => {
		const formatMap = {
			day: 'YYYY-MM-DD',
			month: 'YYYY-MM',
			year: 'YYYY',
		};

		const grouped = groupBy(data, (item) =>
			moment(item.date).format(formatMap[dateGranularity])
		);

		return Object.entries(grouped).map(([dateKey, items]) => {
			const aggregatedPoint: any = { date: dateKey };

			chartSeries.forEach((serie) => {
				aggregatedPoint[serie.key] = items.reduce(
					(sum, item) => sum + (item[serie.key] || 0),
					0
				);
			});

			return aggregatedPoint;
		});
	}, [data, dateGranularity, chartSeries]);


	const getGraph = () => (
		<div className='ResultGraph' style={{ width: !!modalHook.modalData ? '90vw' : '100%' }}>
			<div className="ResultGraph-button-group flex-row">
				<button
					ref={triggerRef}
					className='ResultGraph-button'
					onClick={() => setIsDropdownOpen(prev => !prev)}>
					{isDropdownOpen ? <FaTimes /> : chartSeries[0].type === 'Line' ? <FaChartLine title='Filtrer' /> : <FaChartBar title='Filtrer' />}

				</button>
				<Dropdown
					triggerRef={triggerRef}
					isOpen={isDropdownOpen}
					onClose={() => setIsDropdownOpen(false)}
					content={
						<div className='w-18'>
							<GraphSeriesFilter
								dateGranularity={dateGranularity}
								setDateGranularity={setDateGranularity}
								visibleKeys={visibleKeys}
								setVisibleKeys={setVisibleKeys}
								keyToLabelMap={keyToLabelMap}
								chartSeries={chartSeries}
							/>
						</div>
					}
				/>

				{!modalHook.modalData &&
					<button className='ResultGraph-button' onClick={() => modalHook.openModal({
						title,
						content: getGraph()

					})}>
						<FaExpand title='Agrandir' />
					</button>
				}
			</div>

			<ResponsiveContainer className="ResponsiveContainer" width="100%" height={!!modalHook.modalData ? 500 : 300}>
				<ComposedChart data={aggregatedData}>
					<CartesianGrid stroke="#d9edf7" />
					<XAxis dataKey="date"
						tickFormatter={(value) => {
							const formatMap = {
								day: 'D MMM YYYY',
								month: 'MM YYYY',
								year: 'YYYY',
							};
							return moment(value).format(formatMap[dateGranularity]);
						}}
					/>
					<YAxis />
					<Tooltip formatter={(value, name) => [value, keyToLabelMap[name as keyof typeof keyToLabelMap] || name]} />
					<Legend formatter={(value) => keyToLabelMap[value as keyof typeof keyToLabelMap] || value} />

					{
						chartSeries.map(serie => {
							if (!visibleKeys.has(serie.key)) return null;
							return serie.type === 'Bar' ?
								<Bar dataKey={serie.key} barSize={20} fill={serie.color} key={serie.key} /> :
								<Line type="monotone" dataKey={serie.key} stroke={serie.color} key={serie.key} />
						})
					}
				</ComposedChart>
			</ResponsiveContainer>

		</div>
	);
	return (
		<>
			<Modal
				className='w-80 minH-80'
				onClose={modalHook.closeModal}
				data={modalHook.modalData}
			/>
			{
				!modalHook.modalData && getGraph()
			}
		</>
	);
}

export default ResultGraph;
